import {
    getAccessToken,
    authedFetch,
    dataProviderWithCustomMethod,
} from '@/providers/loginWithEmail/dataProvider';

export type ReceiptCategory = { name: string; subcategories: string[] };
export type Receipt = {
    id: number;
    original_filename: string;
    content_type?: string;
    storage_backend?: 'local' | 'onedrive';
    project_id: number | null;
    project_no?: number | null;
    allocation_type: 'project_cost' | 'company_expense';
    category: string | null;
    subcategory: string | null;
    merchant: string | null;
    amount: string | null;
    transaction_date: string | null;
    match_reason?: string | null;
    duplicate_of_receipt_id?: number | null;
    responsible_user_id: number;
    created_by?: number | null;
    status: string;
    notes?: string | null;
    description?: string | null;
    allocation_status: 'pending' | 'verified' | 'needs_correction';
    allocation_reviewed_by: number | null;
    allocation_reviewed_at: string | null;
    allocation_review_notes: string | null;
    created_at: string;
    qbo_pushed_at?: string | null;
    qbo_push_error?: string | null;
};
export type ExpenseTransaction = {
    id: number;
    source_network: 'AMEX' | 'VISA';
    transaction_date: string;
    description: string;
    amount: string;
    card_last4: string | null;
    cardholder: string | null;
    expense_account?: string | null;
    responsible_user_id: number | null;
    receipt_id: number | null;
    status: string;
    discrepancy: string | null;
    user_credit_card_id: number | null;
    card_account?: string | null;
    posted_at?: string | null;
    posted_by?: number | null;
    post_note?: string | null;
    allocation_type?: 'project_cost' | 'company_expense' | null;
    project_id?: number | null;
    category?: string | null;
    subcategory?: string | null;
    review_note?: string | null;
    qbo_pushed_at?: string | null;
    ignored_at?: string | null;
};
export type ReconciliationSummary = {
    posted: number;
    saved_receipts: number;
    matched: number;
    discrepancies: number;
    missing_receipts: number;
    unmatched_uploads: number;
    approved: number;
    needs_account: number;
    last_charge_date?: string | null;
};
export type ResponsibleUser = {
    id: number;
    name: string | null;
    email: string;
    has_card?: boolean;
    /** The current user is set up to help this coworker with receipts. */
    helped?: boolean;
    /** This coworker is set up to help the current user with receipts. */
    helps_me?: boolean;
};
export type EmployeeScore = {
    responsible_user_id: number | null;
    missing_count?: number;
    missing_amount?: string;
    pending_count?: number;
    pending_amount?: string;
    approved_count?: number;
    approved_amount?: string;
};
export type ReceiptExtraction = {
    merchant: string | null;
    amount: string | null;
    transaction_date: string | null;
    category: string | null;
    subcategory: string | null;
    project_id: number | null;
};
export type ReceiptAllocationChanges = {
    allocation_type?: 'project_cost' | 'company_expense';
    qbo_class_id?: string | null;
    project_id?: number | null;
    category?: string | null;
    subcategory?: string | null;
    notes?: string;
};

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    if (init.body && !(init.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
    }
    let response: Response;
    try {
        response = await authedFetch(path, { ...init, headers });
    } catch (error) {
        if (
            (error instanceof DOMException || error instanceof Error) &&
            error.name === 'AbortError'
        ) {
            throw error;
        }
        throw new Error('Network unavailable. Try again when online.');
    }
    if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const detail = payload.detail;
        throw new Error(
            typeof detail === 'string' ? detail : 'Receipt request failed'
        );
    }
    return response.json();
}

// ponytail: session blob cache so match-dialog re-views skip the network.
// Unmatched list is ≤100; memory is fine for SPA lifetime. Failed fetches are not cached.
const receiptBlobCache = new Map<number, Blob>();
const receiptBlobInflight = new Map<number, Promise<Blob>>();

/** Clears the in-memory receipt blob cache (tests / logout). */
export function clearReceiptBlobCache(): void {
    receiptBlobCache.clear();
    receiptBlobInflight.clear();
}

export async function fetchReceiptBlob(id: number): Promise<Blob> {
    const cached = receiptBlobCache.get(id);
    if (cached) return cached;

    const inflight = receiptBlobInflight.get(id);
    if (inflight) return inflight;

    const promise = (async () => {
        const response = await authedFetch(
            `/receipts/uploads/${id}/content`,
        );
        if (!response.ok) {
            throw new Error('Could not open receipt');
        }
        const blob = await response.blob();
        receiptBlobCache.set(id, blob);
        return blob;
    })().finally(() => {
        receiptBlobInflight.delete(id);
    });

    receiptBlobInflight.set(id, promise);
    return promise;
}

export async function openReceipt(id: number): Promise<void> {
    const popup = window.open('', '_blank');
    if (popup) popup.opener = null;
    try {
        const blob = await fetchReceiptBlob(id);
        const url = URL.createObjectURL(blob);
        if (popup) {
            popup.location.href = url;
        } else {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
        popup?.close();
        throw error;
    }
}

export const fetchReceiptCategories = () =>
    request<ReceiptCategory[]>('/receipts/categories');

export const fetchResponsibleUsers = () =>
    request<ResponsibleUser[]>('/receipts/responsible-users');

export const fetchReceipts = (filters: {
    mine?: boolean;
    transactionDate?: string;
    status?: string;
    deletedAt?: null;
} = {}) => {
    const filter: Record<string, unknown> = {};
    if (filters.mine) filter.mine = true;
    if (filters.transactionDate) {
        filter.transaction_date = filters.transactionDate;
    }
    if (filters.status) filter.status = filters.status;
    if (filters.deletedAt === null) filter.deleted_at = null;
    const params = new URLSearchParams({
        filter: JSON.stringify(filter),
        sort: JSON.stringify(['id', 'DESC']),
        range: JSON.stringify([0, 999]),
    });
    return request<Receipt[]>(`/receipts?${params.toString()}`);
};

/** Client abort for GPT field scan — employees should not wait longer than this. */
export const EXTRACT_TIMEOUT_MS = 60_000;

export const extractReceipt = async (files: File | File[]) => {
    const list = Array.isArray(files) ? files : [files];
    const form = new FormData();
    for (const file of list) {
        form.append('files', file);
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), EXTRACT_TIMEOUT_MS);
    try {
        return await request<ReceiptExtraction>('/receipts/extract', {
            method: 'POST',
            body: form,
            signal: controller.signal,
        });
    } catch (error) {
        if (
            (error instanceof DOMException || error instanceof Error) &&
            error.name === 'AbortError'
        ) {
            throw new Error('Scan timed out.');
        }
        throw error;
    } finally {
        clearTimeout(timer);
    }
};

type ReceiptPdfSplit = {
    page_count: number;
    pages: { filename: string; content_base64: string }[];
};

/** Split a multi-page PDF into one File per page; a 1-page PDF returns [file]. */
export const splitReceiptPdf = async (file: File): Promise<File[]> => {
    const form = new FormData();
    form.append('file', file);
    const result = await request<ReceiptPdfSplit>('/receipts/split-pdf', {
        method: 'POST',
        body: form,
    });
    if (result.pages.length === 0) return [file];
    return result.pages.map(
        page =>
            new File(
                [Uint8Array.from(atob(page.content_base64), c => c.charCodeAt(0))],
                page.filename,
                { type: 'application/pdf' }
            )
    );
};

export const uploadReceipt = (form: FormData) =>
    request<Receipt>('/receipts', { method: 'POST', body: form });

export const updateReceipt = (
    receiptId: number,
    changes: Partial<Pick<
        Receipt,
        'allocation_type' | 'project_id' | 'category' | 'subcategory' |
        'merchant' | 'amount' | 'transaction_date' | 'responsible_user_id' | 'notes'
    >>
) =>
    request<Receipt>(`/receipts/uploads/${receiptId}`, {
        method: 'PATCH',
        body: JSON.stringify(changes),
    });

export async function deleteReceipt(
    receiptId: number,
    reason: string
): Promise<void> {
    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    let response: Response;
    try {
        response = await authedFetch(`/receipts/uploads/${receiptId}`, {
            method: 'DELETE',
            headers,
            body: JSON.stringify({ reason }),
        });
    } catch {
        throw new Error('Network unavailable. Try again when online.');
    }
    if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const detail = payload.detail;
        throw new Error(
            typeof detail === 'string' ? detail : 'Receipt request failed'
        );
    }
}

export const reviewReceiptAllocation = (
    receiptId: number,
    decision: 'verified' | 'needs_correction',
    changes: ReceiptAllocationChanges = {}
) =>
    request<Receipt>(`/receipts/uploads/${receiptId}/review-allocation`, {
        method: 'POST',
        body: JSON.stringify({ decision, ...changes }),
    });

export const retryReceiptQboPush = (receiptId: number) =>
    request<Receipt>(`/receipts/uploads/${receiptId}/qbo-push`, { method: 'POST' });

export const unlockReceipt = (receiptId: number) =>
    request<Receipt>(`/receipts/uploads/${receiptId}/unlock`, { method: 'POST' });

export const postMyTransaction = (
    transactionId: number,
    note: string,
    allocation?: ReceiptAllocationChanges
) =>
    request<ExpenseTransaction>(`/receipts/transactions/${transactionId}/my-post`, {
        method: 'POST',
        body: JSON.stringify({ note: note || null, allocation: allocation ?? null }),
    });

export const classifyTransaction = (
    transactionId: number,
    allocation: ReceiptAllocationChanges
) =>
    request<ExpenseTransaction>(`/receipts/transactions/${transactionId}/classify`, {
        method: 'POST',
        body: JSON.stringify(allocation),
    });

export const returnTransaction = (transactionId: number, note: string) =>
    request<ExpenseTransaction>(`/receipts/transactions/${transactionId}/return`, {
        method: 'POST',
        body: JSON.stringify({ note }),
    });

export const fetchTransactions = (filters: {
    status?: string;
    responsibleUserId?: number;
} = {}) => {
    const filter: Record<string, unknown> = {};
    if (filters.status) filter.status = filters.status;
    if (filters.responsibleUserId != null) {
        filter.responsible_user_id = filters.responsibleUserId;
    }
    const params = new URLSearchParams({
        filter: JSON.stringify(filter),
        sort: JSON.stringify(['transaction_date', 'DESC']),
        range: JSON.stringify([0, 999]),
    });
    return request<ExpenseTransaction[]>(
        `/receipts/transactions/list?${params.toString()}`
    );
};

export async function fetchRequestedTransactionId(): Promise<number | null> {
    const token = getAccessToken();
    if (!token) return null;
    const notices = await dataProviderWithCustomMethod.getNotifications(true);
    for (const notice of notices) {
        if (notice.kind !== 'missing_receipt' || !notice.link) continue;
        const id = Number(
            new URLSearchParams(notice.link.split('?')[1] || '').get('transaction')
        );
        if (id) return id;
    }
    return null;
}

export const fetchReconciliationSummary = () =>
    request<ReconciliationSummary>('/receipts/reconcile/summary');

export const fetchEmployeeScoreboard = (dates: {
    transaction_date_gte?: string;
    transaction_date_lte?: string;
}) => {
    const params = new URLSearchParams(
        Object.entries(dates).filter((entry): entry is [string, string] => !!entry[1])
    );
    return request<EmployeeScore[]>(`/receipts/reconcile/by-employee?${params.toString()}`);
};

export type UnassignedCard = {
    card_account: string | null;
    card_last4: string | null;
    count: number;
    amount: string;
    card_in_users: boolean;
    account_owner_id: number | null;
};

export const fetchUnassignedByCard = () =>
    request<UnassignedCard[]>('/receipts/reconcile/unassigned-by-card');

export const setCardAccountOwner = (cardAccount: string, userId: number | null) =>
    request<{ assigned: number }>('/receipts/card-accounts/owner', {
        method: 'PUT',
        body: JSON.stringify({ card_account: cardAccount, user_id: userId }),
    });

export type StatementImport = {
    period: string;
    cards: {
        card_last4: string;
        cardholder: string;
        user_name: string | null;
        lines: number;
        matched: number;
        matched_amount: string;
    }[];
    matched: number;
    ambiguous: number;
    left_unassigned: number;
    applied: boolean;
    assigned: number;
    receipts_linked: number;
    statement_only: number;
    disagreed: number;
};

const importStatement = (path: string, file: File, apply: boolean) => {
    const form = new FormData();
    form.append('file', file);
    form.append('apply', String(apply));
    return request<StatementImport>(path, { method: 'POST', body: form });
};

export const importChaseStatement = (file: File, apply: boolean) =>
    importStatement('/receipts/reconcile/chase-statement', file, apply);

export const importAmexStatement = (file: File, apply: boolean) =>
    importStatement('/receipts/reconcile/amex-statement', file, apply);

export const bulkAssignTransactions = (transactionIds: number[], userId: number | null) =>
    request<{ assigned: number }>('/receipts/transactions/assign', {
        method: 'POST',
        body: JSON.stringify({ transaction_ids: transactionIds, responsible_user_id: userId }),
    });

export const importExpenseReports = () =>
    request<{
        files_imported: number;
        files_skipped: number;
        transactions_imported: number;
        reports_found: string[];
        errors: string[];
    }>(
        '/receipts/reports/import',
        { method: 'POST' }
    );

export const reconcileReceipts = () =>
    request<ReconciliationSummary>('/receipts/reconcile', { method: 'POST' });

export async function downloadChargesCsv(): Promise<void> {
    const response = await authedFetch('/receipts/transactions/export', {
        headers: { Accept: 'text/csv' },
    });
    if (!response.ok) {
        throw new Error('Could not download the charges.');
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'mrt-charges.csv';
    link.click();
    URL.revokeObjectURL(url);
}

export const requestMissingReceipt = (transactionId: number) =>
    request<{ notification_id: number; created: boolean }>(
        `/receipts/transactions/${transactionId}/request-receipt`,
        { method: 'POST' }
    );

export const assignTransactionUser = (transactionId: number, userId: number | null) =>
    request<ExpenseTransaction>(`/receipts/transactions/${transactionId}`, {
        method: 'PATCH',
        body: JSON.stringify({ responsible_user_id: userId }),
    });

export const ignoreTransaction = (transactionId: number, ignored: boolean) =>
    request<ExpenseTransaction>(`/receipts/transactions/${transactionId}/ignore`, {
        method: 'PUT',
        body: JSON.stringify({ ignored }),
    });

export const matchReceipt = (transactionId: number, receiptId: number) =>
    request<ExpenseTransaction>(
        `/receipts/transactions/${transactionId}/match/${receiptId}`,
        { method: 'POST' }
    );

export const matchMyReceipt = (transactionId: number, receiptId: number) =>
    request<ExpenseTransaction>(
        `/receipts/transactions/${transactionId}/my-match/${receiptId}`,
        { method: 'POST' }
    );

export const unmatchMyReceipt = (transactionId: number) =>
    request<ExpenseTransaction>(
        `/receipts/transactions/${transactionId}/my-match`,
        { method: 'DELETE' }
    );

export type QuickBooksStatus = {
    configured: boolean;
    connected: boolean;
    environment: string;
    realm_id: string | null;
    company_name: string | null;
    connected_at: string | null;
    warning: string | null;
    redirect_uri: string | null;
    credit_card_accounts: Array<{
        id: string;
        name: string;
        account_type: string;
        fully_qualified_name: string | null;
    }>;
};

export const fetchQuickBooksStatus = () =>
    request<QuickBooksStatus>('/quickbooks/status');

export const syncQuickBooksCharges = () =>
    request<
        ReconciliationSummary & {
            imported: number;
            updated: number;
            skipped: number;
        }
    >('/quickbooks/sync', { method: 'POST' });

export const syncQuickBooksProjects = () =>
    request<{
        created: number;
        linked: number;
        updated: number;
        unchanged: number;
        failed: number;
        ambiguous: number;
        unmatched: number;
        classes: number;
        expense_accounts: number;
    }>('/quickbooks/sync-projects', { method: 'POST' });

export type QuickBooksClass = {
    qbo_id: string;
    name: string;
    is_job: boolean;
};
export type QuickBooksProjectClass = {
    project_id: number;
    project_no: number | null;
    name: string;
    phase?: string;
    address: string;
    owner_contractor: string;
    proposed_class_name: string;
    qbo_class_id: string | null;
    qbo_class_name: string | null;
    qbo_class_ids?: string[];
    qbo_class_names?: string[];
    legacy_mrt_construction_qb?: boolean;
};

export const fetchQuickBooksClassMap = () =>
    request<{ projects: QuickBooksProjectClass[]; classes: QuickBooksClass[] }>(
        '/quickbooks/project-classes'
    );

export const assignQuickBooksProjectClass = (
    projectId: number,
    qboClassId: string | null
) =>
    request<QuickBooksProjectClass>(`/quickbooks/projects/${projectId}/class`, {
        method: 'PATCH',
        body: JSON.stringify({ qbo_class_id: qboClassId }),
    });

/** Replace every building Class on a community job (one MRT project, many Classes). */
export const assignQuickBooksProjectClasses = (
    projectId: number,
    qboClassIds: string[]
) =>
    request<QuickBooksProjectClass>(`/quickbooks/projects/${projectId}/class`, {
        method: 'PATCH',
        body: JSON.stringify({ qbo_class_ids: qboClassIds }),
    });

export const createQuickBooksProjectClass = (
    projectId: number,
    name?: string
) =>
    request<QuickBooksProjectClass>(
        `/quickbooks/projects/${projectId}/create-class`,
        {
            method: 'POST',
            body: JSON.stringify({ name: name ?? null }),
        }
    );

/** One-time: label active jobs with no Roofing Class as Legacy MRT Construction QB. */
export const markUnlinkedProjectsLegacy = () =>
    request<{ marked: number }>('/quickbooks/mark-unlinked-legacy', {
        method: 'POST',
    });

export const fetchQuickBooksConnect = () =>
    request<{ authorize_url: string }>('/quickbooks/connect');

export const disconnectQuickBooks = () =>
    request<QuickBooksStatus>('/quickbooks/disconnect', { method: 'POST' });
